import { useCallback, useEffect, useMemo, useState } from "react";

import type { PaymentIntent } from "@chargebee/chargebee-js-types/cb-types/internal/payment-intent/types";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  InfoBar,
  LogSentryException,
  Spinner,
  useRemoteConfig,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import type { UseFormRegisterReturn } from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

import { ReactComponent as Icon_ApplePay24 } from "../../assets/images/ApplePay24.svg";
import { ReactComponent as Icon_CreditCardFilled24 } from "../../assets/images/CreditCardFilled24.svg";
import { ReactComponent as Icon_GooglePay800 } from "../../assets/images/GooglePay800.svg";
import {
  useBilling,
  useUpdateCardFinalizeMutation,
  useUpdateCardStartMutation,
} from "../../hooks/useBilling";
import usePrevious from "../../hooks/usePrevious";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import {
  APPLE_PAY_NOT_SUPPORTED,
  ApplePayButtonId,
  ChargebeePaymentType,
  GOOGLE_PAY_NOT_SUPPORTED,
  GooglePayButtonId,
  PaymentTypes,
  useChargebee,
} from "../../lib/ChargebeeAdapter";
import { Modal } from "../../lib/modal";
import { ElementClickAction, ElementClickLocation } from "../../lib/TrackingAdapter";
import { mapChargebeeErrorMessages } from "../ChargebeeCheckout/ChargebeeCheckoutSchema";
import CreditCardInput from "../CreditCardInput/CreditCardInput";
import type { CreditCardInputValue } from "../CreditCardInput/CreditCardInputTypes";
import { Tiles } from "../Tiles/Tiles";

import style from "./PaymentUpdateModal.module.css";

type PaymentUpdateModalProps = {
  teamID: string | undefined;
  onClose: () => void;
  onSuccess?: () => void;
  open: boolean;
  initialError?: React.ReactNode;
  tertiaryText?: string;
  ctaText?: string;
  onlyCardUpdate?: boolean;
};

const createSchema = (
  t: ReturnType<typeof useTranslation>["t"],
  m: ReturnType<typeof useTranslation>["m"]
) =>
  z
    .object({
      paymentType: z
        .enum(PaymentTypes)
        .nullable()
        .optional()
        .superRefine((val, ctx) => {
          if (!val) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(m.Checkout.SelectPaymentMethod),
              path: [],
            });
          }
        }),
      card: z.custom<CreditCardInputValue>().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.paymentType !== ChargebeePaymentType.Card) {
        return;
      }

      if (!data.card) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          type: "string",
          minimum: 1,
          inclusive: true,
          exact: false,
          message: t(m.Checkout.ProvideCardInfo),
          path: ["card"],
        });
      }

      if (data.card?.error) {
        ctx.addIssue({ code: "custom", message: data.card.error.message, path: ["card"] });
      }
    });

export type PaymentUpdateFormData = z.infer<ReturnType<typeof createSchema>>;

export default function PaymentUpdateModal({
  teamID,
  onClose,
  onSuccess,
  open,
  initialError,
  tertiaryText,
  ctaText,
}: PaymentUpdateModalProps) {
  const { t, m } = useTranslation();
  const schema = useMemo(() => createSchema(t, m), [t, m]);
  const resolvedCtaText = ctaText ?? t(m.TeamBilling.Save);
  const {
    isLoading: isChargebeeLoading,
    isApplePaySupported,
    isGooglePaySupported,
  } = useChargebee();
  const googlePayFlag = useRemoteConfig("web_google_pay_checkout-cd");
  const isGooglePayEnabled = isGooglePaySupported && googlePayFlag === "treatment";

  const methods = useForm<PaymentUpdateFormData>({
    resolver: zodResolver(schema),
    values: {
      paymentType:
        !isChargebeeLoading && !isApplePaySupported ? ChargebeePaymentType.Card : undefined,
    },
  });

  const tracker = useTracker();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isSubmitted },
    reset,
    setError,
    clearErrors,
    watch,
  } = methods;

  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(null);

  const [submitPaymentErrorMessage, setSubmitPaymentErrorMessage] = useState<
    { title: string; description: string } | undefined
  >(undefined);

  const paymentType = watch("paymentType");
  const {
    mutateAsync: mutateUpdateCardStart,
    error: updateCardStartError,
    reset: resetUpdateCardStart,
    isPending: isUpdateCardStartPending,
  } = useUpdateCardStartMutation({ teamID, paymentType });

  const {
    mutateAsync: mutateUpdateCardFinalize,
    isError: isUpdateCardFinalizeError,
    reset: resetUpdateCardFinalize,
  } = useUpdateCardFinalizeMutation(teamID, {
    onSuccess: () => {
      toast.success(t(m.Checkout.PaymentError.PaymentInfoUpdated));
      onSuccess?.();
      onClose();

      if (paymentType) {
        tracker.trackUpdatePaymentSourceSuccess({
          paymentType,
        });
      } else {
        LogSentryException({
          messagePrefix: "Payment update modal success",
          error: new Error("Payment type is undefined on success"),
        });
      }
    },
    onError: (error) => {
      if (error instanceof Error) {
        if (error.name === APPLE_PAY_NOT_SUPPORTED || error.message === APPLE_PAY_NOT_SUPPORTED) {
          tracker.trackApplePaySetupFailed({
            location: "payment_update_modal",
          });
          setSubmitPaymentErrorMessage({
            title: t(m.Checkout.PaymentError.ApplePaySetupFailedTitle),
            description: t(m.Checkout.PaymentError.ApplePaySetupFailed),
          });
          return;
        }

        if (error.name === GOOGLE_PAY_NOT_SUPPORTED || error.message === GOOGLE_PAY_NOT_SUPPORTED) {
          tracker.trackGooglePaySetupFailed({
            location: "payment_update_modal",
          });
          setSubmitPaymentErrorMessage({
            title: t(m.Checkout.PaymentError.GooglePaySetupFailedTitle),
            description: t(m.Checkout.PaymentError.GooglePaySetupFailed),
          });
          return;
        }

        const errorMessages = mapChargebeeErrorMessages(error.message);
        // If mapCheckoutErrorMessages returns undefined error will be displayed in the input
        if (!errorMessages) {
          setError("card", { message: error.message });
        } else {
          setSubmitPaymentErrorMessage({
            title: t(errorMessages.titleMessage),
            description: t(errorMessages.descriptionMessage),
          });
        }
      }
    },
  });

  const { data: billingData } = useBilling(teamID);

  const resetPaymentIntent = useCallback(async () => {
    resetUpdateCardStart();
    resetUpdateCardFinalize();
    if (billingData?.primaryPaymentSource) {
      try {
        const { paymentIntent } = await mutateUpdateCardStart();
        setPaymentIntent(paymentIntent);
      } catch {
        // Handled by react-query
      }
    }
  }, [
    resetUpdateCardStart,
    resetUpdateCardFinalize,
    mutateUpdateCardStart,
    billingData?.primaryPaymentSource,
  ]);

  const previousPaymentType = usePrevious(paymentType);
  useEffect(() => {
    if (paymentType && paymentType !== previousPaymentType && previousPaymentType !== undefined) {
      (async () => {
        clearErrors("card");
        resetPaymentIntent();
      })();
    }
  }, [clearErrors, paymentType, previousPaymentType, resetPaymentIntent]);

  useEffect(() => {
    if (open) {
      (async () => {
        reset();
        resetPaymentIntent();
      })();
    }
  }, [open, reset, resetPaymentIntent]);

  const isApplePayModalOpen = paymentType === ChargebeePaymentType.ApplePay && isSubmitting;

  const isGooglePayModalOpen = paymentType === ChargebeePaymentType.GooglePay && isSubmitting;

  const creditCardInputRegisterProps: Omit<UseFormRegisterReturn<"card">, "ref"> &
    Partial<Pick<UseFormRegisterReturn<"card">, "ref">> = register("card");
  delete creditCardInputRegisterProps.ref; // This is a hack to not throw console errors

  return (
    <Modal
      open={open}
      title={t(m.Checkout.PaymentMethod)}
      size="medium"
      onClose={onClose}
      telemetryName="payment_update"
    >
      <FormProvider {...methods}>
        <form
          className={style.paymentUpdateForm}
          onSubmit={handleSubmit(async (data: PaymentUpdateFormData) => {
            try {
              tracker.trackButtonClick({
                action: ElementClickAction.CONFIRM,
                location: ElementClickLocation.EDIT_PAYMENT_INFORMATION_MODAL,
                details: paymentType ? paymentType : undefined,
              });
              await mutateUpdateCardFinalize({
                data,
                paymentIntent,
                teamID,
              });
            } catch (error) {
              // This is to not throw Unhandled Promise Rejection error
            }
          })}
        >
          {(isApplePaySupported || isGooglePayEnabled) && (
            <>
              <Tiles
                registerAttrs={register("paymentType")}
                options={[
                  ...(isApplePaySupported
                    ? [
                        {
                          icon: <Icon_ApplePay24 />,
                          value: ChargebeePaymentType.ApplePay,
                          label: t(m.Checkout.ApplePay),
                        },
                      ]
                    : []),
                  ...(isGooglePayEnabled
                    ? [
                        {
                          icon: <Icon_GooglePay800 className={style.googlePayIcon} />,
                          value: ChargebeePaymentType.GooglePay,
                          label: t(m.Checkout.GooglePay),
                        },
                      ]
                    : []),
                  {
                    icon: <Icon_CreditCardFilled24 />,
                    value: ChargebeePaymentType.Card,
                    label: t(m.Checkout.CreditDebitCard),
                  },
                ]}
              />
              {errors.paymentType && (
                <span className={style.errorMessage}>{errors.paymentType.message}</span>
              )}
            </>
          )}

          {!isSubmitted && !isSubmitting && initialError}

          <div
            className={classNames(
              style.subgroup,
              paymentType !== ChargebeePaymentType.Card && style.hidden
            )}
          >
            <span className={style.title}>{t(m.Checkout.CreditDebitCard)}</span>
            <CreditCardInput {...creditCardInputRegisterProps} control={control} />
            {tertiaryText && <div className={style.tertiary}>{tertiaryText}</div>}
          </div>

          {isUpdateCardFinalizeError && submitPaymentErrorMessage && (
            <InfoBar
              title={submitPaymentErrorMessage.title}
              text={submitPaymentErrorMessage.description}
              variant="critical"
            />
          )}

          <div className={style.actionButtons}>
            <Button
              type="submit"
              loading={isUpdateCardStartPending}
              disabled={!paymentIntent || isSubmitting || Boolean(Object.keys(errors).length)}
            >
              {resolvedCtaText}
            </Button>
          </div>

          {(isSubmitting || isChargebeeLoading) && (
            <div className={style.overlay}>
              <Spinner />
              {isSubmitting && paymentType === ChargebeePaymentType.Card && (
                <span>{t(m.Checkout.ContactingCardIssuer)}</span>
              )}
            </div>
          )}

          {updateCardStartError && (
            <div className={style.overlay}>
              <InfoBar variant="critical" title={t(m.Checkout.SomethingWentWrongRetry)} />
            </div>
          )}
        </form>
        <Modal
          title={t(m.Checkout.ApplePay)}
          telemetryName="payment_update_apple_pay_confirmation"
          open={isApplePayModalOpen}
          size="small"
          onClose={() => {
            reset(undefined, { keepDirtyValues: true });
            resetUpdateCardFinalize();
          }}
        >
          <div className={style.applePayButtonContainer}>
            <Button className={style.applePayButtonPlaceholder} loading />
            <div id={ApplePayButtonId} />
          </div>
        </Modal>
        <Modal
          title={t(m.Checkout.GooglePay)}
          open={isGooglePayModalOpen}
          size="small"
          onClose={() => {
            reset(undefined, { keepDirtyValues: true });
            resetUpdateCardFinalize();
          }}
          telemetryName="payment_update_google_pay_confirmation"
        >
          <div className={style.googlePayButtonContainer}>
            <Button className={style.googlePayButtonPlaceholder} loading />
            <div id={GooglePayButtonId} />
          </div>
        </Modal>
      </FormProvider>
    </Modal>
  );
}
